import { GraphQLClient } from 'graphql-request'
import { csrfToken } from './axios'
import baseUrl from './baseUrl'

// Helper function to check if an object contains File or Blob objects
const hasFiles = obj => {
  if (obj instanceof File || obj instanceof Blob) return true
  if (Array.isArray(obj)) return obj.some(hasFiles)
  if (obj && typeof obj === 'object') {
    return Object.values(obj).some(hasFiles)
  }
  return false
}

// Helper function to create FormData for multipart uploads
const createMultipartFormData = (query, variables) => {
  const formData = new FormData()

  // Add the operations (query and variables without files)
  const operations = {
    query,
    variables: extractNonFiles(variables),
  }
  formData.append('operations', JSON.stringify(operations))

  // Create map for file positions
  const map = {}
  const files = []

  // Extract files and create map
  extractFiles(variables, 'variables', map, files)

  formData.append('map', JSON.stringify(map))

  // Add files to FormData
  files.forEach((file, index) => {
    formData.append(index.toString(), file)
  })

  return formData
}

// Helper function to extract files from variables and replace with null
const extractNonFiles = obj => {
  if (obj instanceof File || obj instanceof Blob) return null
  if (Array.isArray(obj)) return obj.map(extractNonFiles)
  if (obj && typeof obj === 'object') {
    const result = {}
    for (const [key, value] of Object.entries(obj)) {
      result[key] = extractNonFiles(value)
    }
    return result
  }
  return obj
}

// Helper function to extract files and create file map
const extractFiles = (obj, path, map, files) => {
  if (obj instanceof File || obj instanceof Blob) {
    const index = files.length
    files.push(obj)
    map[index] = [path]
    return
  }

  if (Array.isArray(obj)) {
    obj.forEach((item, idx) => {
      extractFiles(item, `${path}.${idx}`, map, files)
    })
    return
  }

  if (obj && typeof obj === 'object') {
    for (const [key, value] of Object.entries(obj)) {
      const newPath = path ? `${path}.${key}` : key
      extractFiles(value, newPath, map, files)
    }
  }
}

const graphQLRequest = (query, variables, endpoint = '/graphql') => {
  const baseURL = baseUrl()

  try {
    const headers = { 'X-CSRF-Token': csrfToken }
    const client = new GraphQLClient(new URL(endpoint, baseURL), {
      headers,
      credentials: 'include',
    })

    // Check if variables contain files
    if (variables && hasFiles(variables)) {
      // Use fetch directly for multipart uploads
      const formData = createMultipartFormData(query, variables)

      return fetch(new URL(endpoint, baseURL).toString(), {
        method: 'POST',
        headers: {
          'X-CSRF-Token': csrfToken,
          // Don't set Content-Type - let browser set it with boundary for multipart
        },
        credentials: 'include',
        body: formData,
      })
        .then(response => {
          if (!response.ok) {
            throw new Error(`HTTP ${response.status}: ${response.statusText}`)
          }
          return response.json()
        })
        .then(result => {
          if (result.errors) {
            throw new Error(result.errors[0]?.message || 'GraphQL Error')
          }
          return result.data
        })
    }

    // Use regular GraphQL client for non-file requests
    return client.request(query, variables)
  } catch (err) {
    throw new Error(err)
  }
}

export default graphQLRequest
